import { useState, useCallback, useEffect, useRef } from 'react'
import { searchClients, searchClientByPhone, searchClientByName } from '@/services/orderApi'
import type { ClientProfile } from '@/services/orderApi'

interface UseClientSearchOptions {
  debounceMs?: number
  minSearchLength?: number
}

interface UseClientSearchReturn {
  clients: ClientProfile[]
  isLoading: boolean
  error: string | null
  searchTerm: string
  search: (term: string) => void
  clearSearch: () => void
  searchByPhone: (phone: string) => Promise<ClientProfile | null>
  searchByName: (name: string) => Promise<ClientProfile[]>
}

export function useClientSearch(options: UseClientSearchOptions = {}): UseClientSearchReturn {
  const { debounceMs = 300, minSearchLength = 2 } = options

  const [searchTerm, setSearchTerm] = useState('')
  const [clients, setClients] = useState<ClientProfile[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // Perform the search
  const performSearch = useCallback(async (term: string) => {
    if (!term || term.length < minSearchLength) {
      setClients([])
      setError(null)
      return
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    setIsLoading(true)
    setError(null)

    try {
      const results = await searchClients(term)
      setClients(results)
    } catch (err: any) {
      // Ignore abort errors
      if (err.name === 'AbortError') return
      
      setError(err.message || 'Failed to search clients')
      setClients([])
      console.error('Client search error:', err)
    } finally {
      setIsLoading(false)
    }
  }, [minSearchLength])

  // Debounced search
  const search = useCallback((term: string) => {
    setSearchTerm(term)
    
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // If term is too short, clear results immediately
    if (!term || term.length < minSearchLength) {
      setClients([])
      setError(null)
      setIsLoading(false)
      return
    }

    // Set loading state immediately for better UX
    setIsLoading(true)

    // Debounce the actual search
    debounceTimerRef.current = setTimeout(() => {
      performSearch(term)
    }, debounceMs)
  }, [debounceMs, minSearchLength, performSearch])

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchTerm('')
    setClients([])
    setError(null)
    setIsLoading(false)
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }, [])

  // Direct phone search (bypasses debounce)
  const searchByPhoneDirect = useCallback(async (phone: string): Promise<ClientProfile | null> => {
    setIsLoading(true)
    setError(null)
    
    try {
      const result = await searchClientByPhone(phone)
      if (result) {
        setClients([result])
      } else {
        setClients([])
      }
      return result
    } catch (err: any) {
      setError(err.message || 'Failed to search by phone')
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Direct name search (bypasses debounce)
  const searchByNameDirect = useCallback(async (name: string): Promise<ClientProfile[]> => {
    setIsLoading(true)
    setError(null)
    
    try {
      const results = await searchClientByName(name)
      setClients(results)
      return results
    } catch (err: any) {
      setError(err.message || 'Failed to search by name')
      return []
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    clients,
    isLoading,
    error,
    searchTerm,
    search,
    clearSearch,
    searchByPhone: searchByPhoneDirect,
    searchByName: searchByNameDirect,
  }
}
